  /**
 * Componente especifico generador a partride de ADF 5.0.0.
 */

import { AppStore, SnackbarWarningAction } from '@alfresco/aca-shared/store';
import { NodePaging, SearchQueryBuilderService, SearchWidget } from '@alfresco/adf-content-services';
import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { Store } from '@ngrx/store';
import { FILTER_PREQUERY_MAX, NULL, OPERATORS, OPERATORS_LOAD } from 'projects/alden-extension/src/lib/models/alden-constants';
import { CustomSearchWidgetSettings } from 'projects/alden-extension/src/lib/models/custom-viewer-config';
import { articulos, conjunciones, escapeLuceneQuery, preposiciones } from 'projects/alden-extension/src/lib/utils/lucene-utils';
import { BehaviorSubject, Subject } from 'rxjs';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { AldenService } from 'projects/alden-extension/src/lib/services/alden.service';
import { executeFunction } from 'projects/alden-extension/src/lib/utils/dynamic-functions';
import { normalizeText } from 'projects/alden-extension/src/lib/utils/string-utils';

@Component({
    selector: 'smt-search-text-prequery',
    templateUrl: '.././search-text/search-text.component.html',
    styleUrls: ['.././search-text/search-text.component.scss'],
    encapsulation: ViewEncapsulation.None,
    host: { class: 'smt-search-text' }
})
export class SMTSearchTextPrequeryComponent implements SearchWidget, OnInit {

    /** The content of the text box. */
    value = '';

    id: string;
    settings: CustomSearchWidgetSettings;
    context: SearchQueryBuilderService;
    startValue: string;
    operator: string = 'OR';
    displayValue$: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);
    displayError$: Subject<string> = new Subject<string>();

    //Custom SMT
    query: string;
    editable: boolean;

    //Multiple
    visible = true;
    excludedTerms: string[] = [];
    chipOperator: string = 'OR';
    removable = true;
    terms: string[] = [];
    addOnBlur = true;
    readonly separatorKeysCodes = [ENTER, COMMA] as const;
    isChecked: boolean;
    useChips: boolean;
    allowOperatorChange: boolean;
        
    filterIsNull: boolean = false;
    nullable: boolean;
    filterNullApplied: boolean = false;

    constructor(protected store: Store<AppStore>, private aldenService: AldenService) {
        this.editable = true;
        this.useChips = true;
        this.allowOperatorChange = true;
        this.nullable = true;
    }

    ngOnInit() {
        this.operator = (this.settings?.operator) ? this.settings?.operator: this.operator;
        if (this.context && this.settings && this.settings?.pattern) {
            const pattern = new RegExp(this.settings?.pattern, 'g');
            const match = pattern.exec(this.context.queryFragments[this.id] || '');

            if (match && match.length > 1) {
                this.value = match[1];
            }

            if (this.startValue) {
                this.setValue(this.startValue);
            }
        }

        if(this.settings && this.settings?.allowUpdateOnChange !== undefined && this.settings?.allowUpdateOnChange !== null) {
            this.editable = this.settings.allowUpdateOnChange;
        }
        
        if(this.settings && this.settings?.useChips !== undefined && this.settings?.useChips !== null) {
            this.useChips = this.settings.useChips;
        }

        if(this.settings && this.settings?.allowOperatorChange !== undefined && this.settings?.allowOperatorChange !== null) {
            this.allowOperatorChange = this.settings.allowOperatorChange;
        }
        
        this.checkIfNullable();
    }

    checkIfNullable(){
        if(this.settings?.nullable !== undefined && this.settings?.nullable !== null) {
            this.nullable =  this.settings.nullable;
        }
    }

    hasValidValue() {
        return !!this.value || this.terms.length > 0 ||this.excludedTerms.length > 0 || this.filterIsNull || this.filterNullApplied;
    }

    getCurrentValue() {
        return this.value;
    }

    getAppliedValue(): any {
        if(!this.useChips)
            return this.value;
        if(this.filterIsNull)
            return NULL;

        const val = [OPERATORS_LOAD[this.chipOperator]];
        val.push(...this.terms);
        if(this.excludedTerms)
            val.push(...this.excludedTerms.map(term => "!"+term));

        return val;
    }
    
    async submitValues() {
        await this.updateQuery();
        this.updateDisplayValue();
    }

    reset() {
        this.value = '';
        this.terms = [];
        this.excludedTerms = [];
        this.filterIsNull = false;
        this.updateQuery();
        this.updateDisplayValue();
    }

    updateDisplayValue(): void {
        if(this.filterIsNull){
            this.displayValue$.next({
                value: 'Sin valor',
                tooltip: 'Sin valor'
            });
        } else if (!this.value && this.terms.length === 0 && this.excludedTerms.length === 0) {
            this.displayValue$.next(null);
        } else {
            if(this.terms.length+this.excludedTerms.length > 1) {
                this.displayValue$.next({
                    value: `${this.terms.length+this.excludedTerms.length} seleccionados`,
                    tooltip: this.terms.join(", ")
                });
            } else {
                const maxLength = 25;
                var value;
                if(this.terms.length > 0){
                    value = this.terms[0];
                }else if(this.excludedTerms.length > 0){
                    value = this.excludedTerms[0];
                }else{
                    value = this.value;
                }
                this.displayValue$.next({
                        value: value.substring(0, maxLength) + (value.length > maxLength ? '...' : ''),
                        tooltip: value
                });
            }
        }
    }

    async updateQuery() {
        if (this.context && this.settings) {
            this.filterNullApplied = this.filterIsNull;
            this.displayError$.next(null);
            this.query = '';
            const fieldsArray: string[] = [];
            if(this.filterIsNull){
                this.settings.field.split(',').forEach((field) => {
                        fieldsArray.push(`(ISNULL:'${field}' OR ISUNSET:'${field}' OR NOT @${field}:[* TO *])`);
                    });
                this.query = '(' + fieldsArray.join(` AND `) + ')';
            }else if(this.value === "*" || (this.terms.length === 1 && this.terms[0] === "*")){
                this.settings.field.split(',').forEach((field) => {
                        fieldsArray.push(`(@${field}:'*' OR @${field}:[* TO *])`);
                    });
                this.query = '(' + fieldsArray.join(` AND `) + ')';
            }else if (this.value || this.terms.length > 0 || this.excludedTerms.length > 0) {
                const value = this.value.trim().replace(/\s+/g, ' ');
                     
                if(value && this.useChips){
                    this.add();
                }

                const fields = this.settings.field.split(',');

                let dictionary: string[][] = [];
                if(this.terms.some(val => this.settings.hasSpecialSeparator ? /[^a-zA-Z0-9]/.test(val) : /\s/.test(val)) 
                    || (this.settings.hasSpecialSeparator ? /[^a-zA-Z0-9]/.test(this.value) : /\s/.test(this.value))){
                    if(this.useChips){
                        dictionary = await this.getCustomUUIDs(this.terms,this.excludedTerms);
                    }else{
                        dictionary = await this.getCustomUUIDs([value]);
                    }
                }else{
                    dictionary = this.useChips ? await this.getCustomUUIDsFromSingleTerms(this.terms) : await this.getCustomUUIDsFromSingleTerms([value]);
                }
                
                if (dictionary && dictionary.every(inner => inner.length === 0)) {
                    this.query = 'TYPE:"cm:content" AND NOT TYPE:"cm:content"'
                }else{
                    dictionary.forEach((v) => {
                        let uuidQueryArray: string[] = [];
                        v.forEach(uuid => {
                            fields.forEach((field) => {
                                uuidQueryArray.push(`+${field}:'${uuid}'`);
                            });
                        })
                        fieldsArray.push('(' + uuidQueryArray.join(' OR ') + ')')
                    });

                    if (fieldsArray.length === 0) {
                        fieldsArray.push(`+${fields[0]}:''`);
                    }

                    this.query = '(' + fieldsArray.join(` ${this.chipOperator} `) + ')';
                }
            }

            this.context.queryFragments[this.id] = this.query;
            this.context.update();
        }
    }

    private formatValue(value: string): string {
        const formatFunction = this.settings.formatFunction;
        if (formatFunction) {
            return executeFunction(formatFunction, value);
        } else {
            return value;
        }
    }

    private getValidatedUUIDsFromResponse(res:NodePaging, extraFields:string[], values:any[]): string[]{
        return res?.list?.entries?.filter((nodeMinimalEntry) => {
            let isValid = true;
            const properties = nodeMinimalEntry.entry.properties;
            if (properties) {
                isValid = false;
                let combinedFieldValues = "";
                extraFields.forEach(extraField => {
                    const field = extraField.replace("@", "");
                    const fieldValue = properties[field];
                    if (fieldValue) {
                        combinedFieldValues += " " + fieldValue.trim().replace(/\s+/g, ' ');
                    }
                });
                values.forEach(value=>{
                    if (normalizeText(combinedFieldValues).includes(normalizeText(value))) {
                        isValid = true;
                    }
                });
            }
            return isValid;
        }).map((nodeMinimalEntry) => nodeMinimalEntry.entry.id);
    }
    //Custom SMT
    getCurrentQuery() {
        return this.query;
    }

    setValue(value: string) {
        this.terms = [];
        this.excludedTerms = [];
        //  Si viene de defaultValue y es un array
        if(value === NULL) {
            this.filterIsNull = true;
        } else if(value && Array.isArray(value))  {
            if(OPERATORS[value[0]]){
                this.chipOperator = OPERATORS[value[0]];
                value.shift();
            }
            value.forEach(v => {  
                this.value = v;
                if(this.value.startsWith("!")){
                    this.value = this.value.slice(1);
                    this.exclude();
                }
                else this.add();
            });
        } else {
            this.value = value;
        }
    }

    getSearchPrefix(): string {
        return this.settings.searchPrefix ? this.settings.searchPrefix : '';
    }

    getSearchSuffix(): string {
        return this.settings.searchSuffix ? this.settings.searchSuffix : '';
    }                                                   

    async getCustomUUIDsFromSingleTerms(values: string[]){
        const dictionary:string[][] = [];
        let fieldsArray:string[] = [];
    
        fieldsArray.push(...this.getValuesQuery(values));

        const bodyParam = this.getUUIDsQuery(fieldsArray,true);

        let res = await this.aldenService.callAlfrescoSearchApi(bodyParam);
        
        if (res?.list?.pagination?.hasMoreItems) {
            this.displayError$.next("Debe afinar la búsqueda");
            const snackbar = new SnackbarWarningAction("No se pudo aplicar alguno de los filtros.");
            snackbar.duration = 10000;
            this.store.dispatch(snackbar);
            return null;
        }

        const UUIDs = this.useChips ? this.getValidatedUUIDsFromResponse(res,this.settings.extraFields,values) : this.getValidatedUUIDsFromResponse(res,this.settings.extraFields,[{value: this.value, excluded: false}]);
        if(UUIDs.length > 0)
            dictionary.push(UUIDs);
        fieldsArray = [];

        return dictionary;
    }
    
    async getCustomUUIDs(values: string[],excluded?: string[], fieldsOverride?: string[]): Promise<string[][]> {
        const dictionary:string[][] = [];
        let fieldsArray:string[] = [];
        
        if(this.settings?.hasSpecialSeparator){
            excluded = excluded?.map(term => term.replace(/[^\p{L}\p{N}]+/gu, " ").trimEnd());
        }

        for(var i=0;i<values.length;i++){
            if(this.settings.combinableFields){
                var combinedValues = this.getCombinedValues(values[i].replace(/[^\p{L}\p{N}]+/gu, " ").trimEnd().split(" "));
                fieldsArray.push(...this.getCombinedValuesQuery(combinedValues,excluded));
                fieldsOverride = this.settings.extraFields.filter(field => !this.settings.combinableFields.includes(field));
            }
            
            fieldsArray.push(...this.getSingleValuesQuery([values[i]],excluded,fieldsOverride));
            
            const bodyParam = this.getUUIDsQuery(fieldsArray);

            let res = await this.aldenService.callAlfrescoSearchApi(bodyParam);
            
            if (res?.list?.pagination?.hasMoreItems) {
                this.displayError$.next("Debe afinar la búsqueda");
                const snackbar = new SnackbarWarningAction("No se pudo aplicar alguno de los filtros.");
                snackbar.duration = 10000;
                this.store.dispatch(snackbar);
                return null;
            }

            const UUIDs = this.getValidatedUUIDsFromResponse(res,this.settings.extraFields,[values[i]]);
            if(UUIDs.length > 0)
                dictionary.push(UUIDs);
            fieldsArray = [];
        }
        
        return dictionary;
    }

    getUUIDsQuery(fieldsArray:string[],singleTerms?:boolean){
        let fieldOperator = singleTerms && this.chipOperator ? this.chipOperator : 'OR';// !this.settings.fieldOperator ? 'OR' : this.settings.fieldOperator;
        
        const query = '(' + fieldsArray.join(` ${fieldOperator} `) + ')';
        const baseQuery = this.settings.extraQuery ? this.settings.extraQuery + ' AND ' : '';
        const bodyParam = {
            query: {
                language: "afts",
                query: `${baseQuery}${query}`
            },
            paging: {
                maxItems: FILTER_PREQUERY_MAX,
            },
            include: ["properties"]
        };
        return bodyParam;
    }
        
    getCombinedValuesQuery(combinedValues: string[][],excluded:string[]): string[] {
        const fieldsToUse = this.settings.combinableFields;
        let fieldsArray: string[] = [];
        combinedValues.forEach(combined => {
            if(combined.length === 1){
                for(var i = 0;i<fieldsToUse.length;i++){
                    fieldsArray.push(`=${fieldsToUse[i]}:'${escapeLuceneQuery(this.formatValue(combined[0]))}'`);
                }
            }else{
                var valuesArray: string[] = [];
                for(var i = 0;i<combined.length;i++){
                    let values = [`${fieldsToUse[i]}:'${escapeLuceneQuery(this.formatValue(combined[i]))}'`]
                    excluded.forEach(ex => {
                        values.push(`-${fieldsToUse[i]}:'${escapeLuceneQuery(this.formatValue(ex))}'`);
                    })
                    valuesArray.push('(' + values.join(` AND `) + ')');
                }
                fieldsArray.push('(' + valuesArray.join(` AND `) + ')');
                if(combined.length < fieldsToUse.length){
                    valuesArray = [];
                    for(var i = 0;i<combined.length;i++){
                        let values = [`${fieldsToUse[i+(fieldsToUse.length - combined.length)]}:'${escapeLuceneQuery(this.formatValue(combined[i]))}'`]
                        excluded.forEach(ex => {
                            values.push(`-${fieldsToUse[i+(fieldsToUse.length - combined.length)]}:'${escapeLuceneQuery(this.formatValue(ex))}'`);
                        });
                        valuesArray.push('(' + values.join(` AND `) + ')');
                    }
                    fieldsArray.push('(' + valuesArray.join(` AND `) + ')');
                } 
            }
        });

        return fieldsArray;
    }

    getSingleValuesQuery(values:string[],excluded?:string[], fieldsOverride?: string[]): string[] {
        const fieldsToUse = fieldsOverride ?? this.settings.extraFields;
        let fieldsArray: string[] = [];
  
        values.forEach(value => {
            var valuesArray: string[] = [];
            fieldsToUse.forEach((f) => {
                if (!conjunciones.includes(value) && !preposiciones.includes(value) && !articulos.includes(value)) {
                    //valuesArray.push(`${f}:'${this.getSearchPrefix()}${escapeLuceneQuery(this.formatValue(value))}${this.getSearchSuffix()}'`);
                    let values = [`${f}:'${escapeLuceneQuery(this.formatValue(value))}'`];
                    excluded.forEach(ex =>{
                        values.push(`-${f}:'${escapeLuceneQuery(this.formatValue(ex))}'`);
                    });
                    valuesArray.push('(' + values.join(` AND `) + ')');
                }
            });
            let operator = this.chipOperator ? this.chipOperator : 'OR';
            fieldsArray.push('(' + valuesArray.join(` ${operator} `) + ')');
        });

        return fieldsArray;
    }
        
    getValuesQuery(values:string[], fieldsOverride?: string[]): string[] {
        const fieldsToUse = fieldsOverride ?? this.settings.extraFields;
        let fieldsArray: string[] = [];
        const excluded = this.excludedTerms?.reduce<string[]>((acc, term) => { acc.push(...term.split(' ')); return acc;}, [])
  
        values.forEach(value => {
            var valuesArray: string[] = [];
            fieldsToUse.forEach((f) => {
                if (!conjunciones.includes(value) && !preposiciones.includes(value) && !articulos.includes(value)) {
                    valuesArray.push(`${f}:'${this.getSearchPrefix()}${escapeLuceneQuery(this.formatValue(value))}${this.getSearchSuffix()}'`);
                }
            });
            fieldsArray.push('(' + valuesArray.join(` OR `) + ')');
        });

        excluded.forEach(value => {
            var valuesArray: string[] = [];
            fieldsToUse.forEach((f) => {
                if (!conjunciones.includes(value) && !preposiciones.includes(value) && !articulos.includes(value)) {
                    valuesArray.push(`-${f}:'${this.getSearchPrefix()}${escapeLuceneQuery(this.formatValue(value))}${this.getSearchSuffix()}'`);
                }
            });
            fieldsArray.push('(' + valuesArray.join(` AND `) + ')');
        });

        return fieldsArray;
    }

    disable() {
        this.editable = false;
    }

    enable() {
        this.editable = true;
    }
        
    add(): void {
        if (this.value && !this.terms.includes(this.value)) {
            this.terms.push(this.value);
            this.value = "";
        }
    }

    exclude(): void {
        if (this.value && !this.excludedTerms.includes(this.value)) {
            this.excludedTerms.push(this.value);
            this.value = "";
        }
    }

    remove(term: string): void {
        const index = this.terms.indexOf(term);
        if (index >= 0) {
            this.terms.splice(index, 1);
        }else{
            const index = this.excludedTerms.indexOf(term);
            if (index >= 0) {
                this.excludedTerms.splice(index, 1);
            }
        }
    }

    getCombinedValues(values: string[]): string[][] {
        if (values.length === 0)
            return [[]];
        const result: string[][] = [];

        for (let i = 1; i <= values.length; i++) {
            const group = values.slice(0, i).join(" ");
            const rest = this.getCombinedValues(values.slice(i));
            for (const val of rest) {
                result.push([group, ...val]);
            }
        }

        return result;
    }

    getPermutations(values: string[]): string[][] {
        if (values.length === 0)
            return [[]];
        const result: string[][] = [];

        for (let i = 0; i < values.length; i++) {
            const firstValue = values[i];
            const rest = [...values.slice(0, i), ...values.slice(i + 1)];
            const perms = this.getPermutations(rest);
            for (const val of perms) {
                result.push([firstValue, ...val]);
            }
        }

        return result;
    }

}